import { useTranslation } from "react-i18next"
import { Select } from "antd"
import { Button } from "../../components"

const T_PREFIX = 'cycle_counts.bulk_assign.filters_step'

export const BulkAssignFiltersStep = ({
    filterOptions,
    filters,
    onFiltersChange,
    onPreview,
    onCancel,
    loading,
}) => {
    const { i18n } = useTranslation()

    const hasAtLeastOneFilter = filters.area?.length > 0 || filters.store_id?.length > 0

    const handleAreaChange = (values) => {
        // When areas change, remove any selected rows that no longer belong to the selected areas
        const newRowOptions = new Set()
        ;(values || []).forEach(area => {
            (filterOptions?.rows_by_area?.[area] || []).forEach(r => newRowOptions.add(r))
        })
        const validRows = (filters.row || []).filter(r => newRowOptions.has(r))
        onFiltersChange({ ...filters, area: values || [], row: validRows })
    }

    const handleRowChange = (values) => {
        onFiltersChange({ ...filters, row: values || [] })
    }

    const handleStoreChange = (values) => {
        onFiltersChange({ ...filters, store_id: values || [] })
    }

    // Merge row options from all selected areas
    const rowOptions = (() => {
        const areas = filters.area || []
        if (areas.length === 0) return []
        const rowSet = new Set()
        areas.forEach(area => {
            (filterOptions?.rows_by_area?.[area] || []).forEach(r => rowSet.add(r))
        })
        return [...rowSet].sort()
    })()

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {i18n.t(`${T_PREFIX}.area_label`)}
                </label>
                <div data-testid="area-select">
                    <Select
                        mode="multiple"
                        className="w-full"
                        placeholder={i18n.t(`${T_PREFIX}.area_placeholder`)}
                        value={filters.area}
                        onChange={handleAreaChange}
                        allowClear
                        showSearch
                    >
                        {(filterOptions?.areas || []).map(area => (
                            <Select.Option key={area} value={area}>{area}</Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {i18n.t(`${T_PREFIX}.row_label`)}
                </label>
                <div data-testid="row-select">
                    <Select
                        mode="multiple"
                        className="w-full"
                        placeholder={i18n.t(`${T_PREFIX}.row_placeholder`)}
                        value={filters.row}
                        onChange={handleRowChange}
                        allowClear
                        disabled={!filters.area?.length}
                    >
                        {rowOptions.map(row => (
                            <Select.Option key={row} value={row}>{row}</Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {i18n.t(`${T_PREFIX}.store_label`)}
                </label>
                <div data-testid="store-select">
                    <Select
                        mode="multiple"
                        className="w-full"
                        placeholder={i18n.t(`${T_PREFIX}.store_placeholder`)}
                        value={filters.store_id}
                        onChange={handleStoreChange}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        {(filterOptions?.stores || []).map(store => (
                            <Select.Option key={store.id} value={store.id}>
                                {store.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <div className="flex justify-between pt-4">
                <Button type="secondary" onClick={onCancel}>
                    {i18n.t('cycle_counts.assign_modal.cancel')}
                </Button>
                <Button
                    type="primary"
                    disabled={!hasAtLeastOneFilter || loading}
                    onClick={onPreview}
                    loading={loading}
                >
                    {i18n.t(`${T_PREFIX}.preview_button`)}
                </Button>
            </div>
        </div>
    )
}
